import { ChangeDetectionStrategy, Component, ElementRef, forwardRef, inject, Input, Renderer2 } from '@angular/core';
import { IProductDataV2 } from '@df/catalog/product/product';
import { DfEventData, DfEventService } from '@df/core/service/df-event';
import { IBuyRequestData } from '@df/session/api/basket/basket';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { UiComponent } from '@df/ui/ui.component';
import { ProductDecorator } from '../../../../model/product/product.decorator';
import {
    PRODUCT_CLOSE_PREVIEW_EVENT_NAME,
    PRODUCT_OPEN_PREVIEW_EVENT_NAME,
    PRODUCT_PREPARE_PREVIEW_EVENT_NAME
} from '../../product.abstract';
import { ProductService } from '../../product.service';
import { ProductListingConfigurableOptionsComponent } from '../configurable-options/product-listing-configurable-options.component';
import { ProductListingAddToBasketButtonComponent } from './button/product-listing-add-to-basket-button.component';

@Component({
    selector: 'product-listing-add-to-basket',
    templateUrl: './product-listing-add-to-basket.component.html',
    styleUrl: './product-listing-add-to-basket.component.scss',
    standalone: true,
    imports: [HrefComponent, ProductListingAddToBasketButtonComponent, forwardRef(() => ProductListingConfigurableOptionsComponent)],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductListingAddToBasketComponent extends UiComponent {
    @Input({ required: true })
    product!: ProductDecorator<IProductDataV2>;

    @Input()
    buyRequest?: IBuyRequestData;

    @Input()
    color?: number;

    @Input()
    listPosition?: number;

    @Input()
    context?: 'wishlist';

    @Input('canAdd')
    _canAdd = true;

    protected override canDetach = true;

    showOptions = false;

    protected dfEventService = inject(DfEventService);
    protected elementRef = inject(ElementRef);
    protected renderer2 = inject(Renderer2);
    protected productService = inject(ProductService);

    get isWishlist() {
        return this.context === 'wishlist';
    }

    get canAdd() {
        if (this.product.hasLetterOption || this.product.isBundle) {
            return false;
        }

        return this._canAdd;
    }

    override ngOnInit(): void {
        this.subscriptions.add(
            this.dfEventService.success(event => {
                if (this.productService.canTriggerEvent(event, this.product, this.listPosition)) {
                    if (event.id === PRODUCT_OPEN_PREVIEW_EVENT_NAME) {
                        this.onOpenEvent(event);
                    }

                    if (event.id === PRODUCT_CLOSE_PREVIEW_EVENT_NAME) {
                        this.onCloseEvent(event);
                    }
                }
            })
        );

        super.ngOnInit();
    }

    handleMouseEnter(): void {
        this.dfEventService.dispatch({
            id: PRODUCT_PREPARE_PREVIEW_EVENT_NAME,
            data: {
                product: this.product,
                index: this.listPosition,
                callback: component => {
                    if (!component.canPreview) {
                        this.dfEventService.dispatch({
                            id: PRODUCT_CLOSE_PREVIEW_EVENT_NAME,
                            data: {
                                product: this.product as ProductDecorator<IProductDataV2>,
                                index: this.listPosition
                            }
                        });
                    }
                }
            },
            action: () => {
                this.showOptions = true;

                this.detectChanges();
            }
        });
    }

    protected toggle(to = false) {
        if (to) {
            this.renderer2.addClass(this.elementRef.nativeElement, 'is-open');
        } else {
            this.renderer2.removeClass(this.elementRef.nativeElement, 'is-open');
        }
    }

    protected onOpenEvent(event: DfEventData) {
        if (this.client.isL || this.client.isX) {
            this.toggle(true);
        }
    }

    protected onCloseEvent(event: DfEventData) {
        this.toggle();
    }
}
